import { Component, Input, OnInit, ViewEncapsulation } from "@angular/core";
import { atcb_action } from "add-to-calendar-button";
import { DateTime } from "luxon";
import { DashboardWidget, WidgetTimelineSlide } from "src/app/shared/model/dashboard-config.model";
import { PublicConfigService } from "src/app/shared/services/public.config.service";
import { StorageService } from "src/app/shared/services/storage.service";
import { WidgetsService } from "src/app/shared/services/widgets.service";

@Component({
  selector: 'widget-timeline',
  templateUrl: './widget-timeline.component.html',
  styleUrls: ['./widget-timeline.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class WidgetTimelineComponent implements OnInit {
  @Input() widgetConfig: DashboardWidget;

  loading: boolean = true;

  FEUrl: string;
  timelineSlides: WidgetTimelineSlide[] = [];

  nowISO: string = DateTime.fromJSDate(new Date()).setZone("UTC").toISO();
  
  competitionName: string;
  competitionTimeZoneDatabaseName: string;

  firstSlideIndex: number = 0;
  activeSlideIndex: number = 0;
  timelineMilestoneBarPosition: number = 0;

  upToDateStages: any[] = [];
  
  constructor(
    private storage: StorageService,
    private publicConfig: PublicConfigService,
    private widgetService: WidgetsService
  ) { }

  async ngOnInit() {
    this.FEUrl = this.publicConfig.feBaseUrl;

    // getting competition properties used in add to calendar
    const competitionName = this.storage.retrieve('competitionName');
    if ((competitionName !== '') && (competitionName !== null)) {
      this.competitionName = competitionName;
    } else {
      this.competitionName = 'Carrot Platform 2.0';
    }
    const competitionTimeZoneDatabaseNameFromStorage = this.storage.retrieve('competitionTimeZoneDatabaseName');
    if ((competitionTimeZoneDatabaseNameFromStorage !== '') && (competitionTimeZoneDatabaseNameFromStorage !== null)) {
      this.competitionTimeZoneDatabaseName = competitionTimeZoneDatabaseNameFromStorage;
    } else {
      this.competitionTimeZoneDatabaseName = 'Etc/GMT';
    }

    // fix for wrong config - if timelineSlides array is missing
    this.timelineSlides = this.widgetConfig.widgetSettings.timelineSlides;
    if (!this.timelineSlides) {
      this.timelineSlides = [];
    }

    // pulling latest data for actual stages
    const stageIds = this.timelineSlides.filter(element => element.stageId !== '0').map(element => {return element.stageId});
    try {
      const resp: any = await this.widgetService.getTimelineStagesData(stageIds).toPromise();
      this.upToDateStages = resp.data;
    } catch (error) {
      console.log(error);
    }

    // local vars for first and last milestone and total distance between them (time, in seconds)
    let firstMilestone: DateTime;
    let lastMilestone: DateTime;
    let timelineDistanceTotal: number;

    if (this.timelineSlides.length > 0) {
      // if there is more than 1 slide compile the local vars
      firstMilestone = DateTime.fromISO(this.timelineSlides[0].endDate);
      lastMilestone = DateTime.fromISO(this.timelineSlides[this.timelineSlides.length - 1].endDate);
      timelineDistanceTotal = lastMilestone.diff(firstMilestone, 'seconds').toObject().seconds;
      // apply styles for first and last milestone
      this.timelineSlides[0].milestonePositionInPercent = 0;
      this.timelineSlides[this.timelineSlides.length - 1].milestonePositionInPercent = 100;
    };

    this.timelineSlides.forEach((element, index) => {
      // this is used to update the stages with the new data from API
      let updatedStage: any = null;
      if (element.stageId !== '0') {
        updatedStage = this.upToDateStages.find(stage => stage.id === element.stageId);
        if (updatedStage) {
          element.endDate = updatedStage.endDate;
          element.title = updatedStage.name;
        }
      }
      // get slide end date (and time)
      // it's important to map the end date coming from RP as UTC zone, otherwise the user's timezone is attached to it
      // as if it was in the user's timezone instead of UTC
      const slideEndDate = DateTime.fromISO(element.endDate, {zone: 'UTC'});

      // define all milestones as not passed - will be updated later (this is here to avoid repetition in the code)
      element.milestonePassed = false;

      // calculate milestone position on timeline
      // for all but the first and the last milestone
      if (index > 0 && index < this.timelineSlides.length - 1) {
        const slideMilestoneDistanceFromFirst = slideEndDate.diff(firstMilestone, 'seconds').toObject().seconds;
        const slideMilestonePosition = (slideMilestoneDistanceFromFirst / timelineDistanceTotal) * 100;
        element.milestonePositionInPercent = slideMilestonePosition;
        // below is a fix for cases of overlapping milestones
        if (element.milestonePositionInPercent - this.timelineSlides[index -1].milestonePositionInPercent < 5) {
          element.milestonePositionInPercent = element.milestonePositionInPercent + (5 - (element.milestonePositionInPercent - this.timelineSlides[index -1].milestonePositionInPercent));
        }
      }

      // edge case fix for the last date of the timeline when there are overlapping dates at the end
      if (this.timelineSlides.length > 2 && index === this.timelineSlides.length - 2) {
        if (this.timelineSlides[this.timelineSlides.length - 1].milestonePositionInPercent - element.milestonePositionInPercent < 5) {
          this.timelineSlides[this.timelineSlides.length - 1].milestonePositionInPercent = this.timelineSlides[this.timelineSlides.length - 1].milestonePositionInPercent + (5 - (this.timelineSlides[this.timelineSlides.length - 1].milestonePositionInPercent - element.milestonePositionInPercent));
        };
      };

      // calculate difference to current time (negative number if end date has passed)
      const slideDiffToNow = slideEndDate.diff(DateTime.fromISO(this.nowISO), 'seconds').toObject().seconds;
      if (slideDiffToNow > 864000) {
        // more than 10 days
        element.calculatedCountdownClass = 'green';
        const calculatedDays = Math.trunc(slideEndDate.diff(DateTime.fromISO(this.nowISO), 'days').toObject().days);
        element.calculatedCountdownText = `${calculatedDays} days left`;
      } else if (slideDiffToNow > 432000) {
        // between 5 and 10 days
        element.calculatedCountdownClass = 'orange';
        const calculatedDays = Math.trunc(slideEndDate.diff(DateTime.fromISO(this.nowISO), 'days').toObject().days);
        element.calculatedCountdownText = `${calculatedDays} days left`;
      } else if (slideDiffToNow > 86400) {
        // between 1 and 5 days
        element.calculatedCountdownClass = 'red';
        const calculatedDays = Math.trunc(slideEndDate.diff(DateTime.fromISO(this.nowISO), 'days').toObject().days);
        element.calculatedCountdownText = calculatedDays === 1 ? `${calculatedDays} day left` : `${calculatedDays} days left`;
      } else if (slideDiffToNow > 3600) {
        // between 1 and 24 hours
        element.calculatedCountdownClass = 'red';
        const calculatedHours = Math.trunc(slideEndDate.diff(DateTime.fromISO(this.nowISO), 'hours').toObject().hours);
        element.calculatedCountdownText = calculatedHours === 1 ? `${calculatedHours} hour left` : `${calculatedHours} hours left`;
      } else if (slideDiffToNow > 0) {
        // less than 1 hour
        element.calculatedCountdownClass = 'red';
        element.calculatedCountdownText = `Less than 1h left`;
      } else {
        // passed
        element.calculatedCountdownClass = 'hidden';
        element.calculatedCountdownText = `${slideDiffToNow}`;
        // if this slide end date has passed, update first slide index and add style for passed milestone
        this.firstSlideIndex++;
        element.milestonePassed = true;
      };

      // prepare proper values for add to calendar button
      // this is needed to have the deadline appear as an event with duration of one hour
      const slideEndDateMinusOneHour = slideEndDate.minus(3600000);
      element.calculatedAddToCalendarStartDate = slideEndDateMinusOneHour.toISODate();
      element.calculatedAddToCalendarStartTime = slideEndDateMinusOneHour.toISOTime({includeOffset: false, suppressSeconds: true, suppressMilliseconds: true});
      element.calculatedAddToCalendarEndDate = slideEndDate.toISODate();
      element.calculatedAddToCalendarEndTime = slideEndDate.toISOTime({includeOffset: false, suppressSeconds: true, suppressMilliseconds: true});
    });

    // adjusting initial position of the timeline bar
    if (this.timelineSlides[this.firstSlideIndex]) {
      this.timelineMilestoneBarPosition = this.timelineSlides[this.firstSlideIndex].milestonePositionInPercent;
    };
    // adjusting initial active milestone
    this.activeSlideIndex = this.firstSlideIndex;

    this.loading = false;
  };

  addToCalendarClick(slide: WidgetTimelineSlide) {
    const config: Object = {
      name: `${this.competitionName} ${slide.title} Deadline`,
      startDate: `${slide.calculatedAddToCalendarStartDate}`,
      startTime: `${slide.calculatedAddToCalendarStartTime}`,
      endDate: `${slide.calculatedAddToCalendarEndDate}`,
      endTime: `${slide.calculatedAddToCalendarEndTime}`,
      options: ["Google", "Apple", "Microsoft365", "Outlook.com", "MicrosoftTeams", "Yahoo", "iCal"],
      timeZone: `${this.competitionTimeZoneDatabaseName}`
    };

    // passed element is needed for autofocus to work after closing the add to calendar modal
    const button: HTMLElement = document.getElementById(`add-to-calendar-${slide.title}`);
    atcb_action(config, button);
  }

  onSlideChange(event: any) {
    this.activeSlideIndex = event.page;
    this.timelineMilestoneBarPosition = this.timelineSlides[this.activeSlideIndex].milestonePositionInPercent;
  }
}
